import { Tooltip, Typography } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useState } from 'react';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import type { IDocumentItemProps, IDocumentsInfo } from '../../interfaces';
import { TranslationKey } from '../../models';
import { ConfirmDeleteDialog } from '../ConfirmDeleteDialog';
import { ThumbWithDetail } from '../ThumbWithDetail';
import { DocumentItemFile } from './DocumentItemFile';
import {
  StyledDeleteIcon,
  StyledDivContent,
  StyledErrorIcon,
  StyledListItem,
  StyledProgress,
  StyledSpanActions,
  StyledSpanInfo,
  StyledSpanThumb,
} from './DocumentItemStyledComponents';

export const DocumentItem: FC<IDocumentItemProps> = (props) => {
  const {
    onDocumentRemove,
    document,
    isShowApprovalState,
    canDelete,
    errorMessage,
    isDeleteConfirmationRequired,
    uploadProgress,
  } = props;

  const [confirmOpened, setConfirmOpened] = useState(false);
  const { getTestIdProps } = useTestId();

  const confirmDelete = () => setConfirmOpened(true);

  const confirmCanceled = () => setConfirmOpened(false);

  const removeDocument = () => {
    if (onDocumentRemove && document.documentId) {
      onDocumentRemove(document.documentId);
    }
  };

  const deleteDocument = () => {
    if (onDocumentRemove) {
      onDocumentRemove(document.documentId);
    }

    setConfirmOpened(false);
  };

  const getDeleteButton = () => {
    const deleteAction = errorMessage || !isDeleteConfirmationRequired ? removeDocument : confirmDelete;

    if (canDelete || errorMessage) {
      return (
        <StyledDeleteIcon
          onClick={deleteAction}
          {...getTestIdProps('document-item-btn-delete')}
        />
      );
    }

    return null;
  };

  const getProgressBar = () => {
    if (uploadProgress) {
      return (
        <StyledProgress
          value={uploadProgress}
          variant="determinate"
        />
      );
    }

    return null;
  };

  const getThumb = () => {
    const { fileName, documentId }: IDocumentsInfo = document;
    if (fileName && documentId) {
      return (
        <ThumbWithDetail
          fileName={fileName}
          documentId={documentId}
        />
      );
    }

    return null;
  };

  const getError = () => {
    if (errorMessage) {
      return (
        <Tooltip title={errorMessage}>
          <StyledErrorIcon />
        </Tooltip>
      );
    }

    return null;
  };

  return (
    <StyledListItem>
      <StyledDivContent>
        <StyledSpanInfo>
          <StyledSpanThumb>{getThumb()}</StyledSpanThumb>
          <DocumentItemFile
            document={document}
            visibleApprovalState={isShowApprovalState}
          />
        </StyledSpanInfo>
        <StyledSpanActions>
          {getError()}
          {getDeleteButton()}
        </StyledSpanActions>
      </StyledDivContent>
      {getProgressBar()}
      <ConfirmDeleteDialog
        opened={confirmOpened}
        handleCancel={confirmCanceled}
        handleConfirm={deleteDocument}
      >
        <Typography
          paragraph
          align="center"
        >
          <Localization
            id={TranslationKey.confirmDocumentDelete}
            defaultMessage={defaultTranslations.confirmDocumentDelete}
          />
        </Typography>
      </ConfirmDeleteDialog>
    </StyledListItem>
  );
};
